import { getAuthCookie } from '../utils/cookies';
import {
    DEFAULT_COMPANY_ID,
    companyDefaultApiUrl,
    companyHasFeature,
    companyHasScreen,
    getCompanyConfig,
    getDefaultApiUrl,
    getDefaultCompany,
    secureApiUrl
} from './companies';

/**
 * The company of the current session: `{ companyId, apiUrl, companyName }`, mirroring the
 * app's session (shelf-app/app/shared/company/session.js).
 *
 * Persisted in localStorage next to the auth cookie (`utils/cookies.js`). The login sets it
 * once central-service has resolved the email; logout and a company switch clear it together
 * with everything else the admin keeps (`./reset.js`).
 *
 * Every API URL and the `x-company` header are read from here per request (`UrlFactory.apiUrl`,
 * `companyHeaders()`), never from a constant. Without a session the default company (Mary)
 * applies, which is exactly the admin as it was before there were several companies.
 */

export const COMPANY_STORAGE_KEY = 'shelf.company';
// Company id of the last session this browser saved. Written on every setCompany and never
// cleared: its presence means this browser already has the company layer, so a signed-in
// session without a stored company is not a pre-update Mary session (see migrateLegacySession).
export const LAST_COMPANY_KEY = 'shelf.lastCompanyId';

// undefined: not loaded yet; null: no company; otherwise the frozen session company
let cached;
// The company the page was loaded for (router, sidebar and module-level labels).
let bootCompanyId = null;
const resetHandlers = new Set();

function storage() {
    try {
        return typeof window !== 'undefined' && window.localStorage ? window.localStorage : null;
    } catch {
        return null;
    }
}

function readItem(key) {
    try {
        return storage()?.getItem(key) ?? null;
    } catch {
        return null;
    }
}

function writeItem(key, value) {
    try {
        storage()?.setItem(key, value);
        return true;
    } catch {
        return false;
    }
}

function removeItem(key) {
    try {
        storage()?.removeItem(key);
    } catch {
        // ignore
    }
}

/**
 * Validate a stored or resolved company. Unknown companies are rejected.
 *
 * The API URL is the registry's (companies/<id>/company.js: its build override, e.g.
 * REACT_APP_API_URL for Mary, else its production URL), whatever central-service or storage
 * says: credentials only ever go to a URL this build was made with. A resolved URL (https only)
 * is used just for a company whose profile has none.
 * @param {any} value
 * @returns {{ companyId: string, apiUrl: string, companyName: string }|null}
 */
export function sanitizeCompany(value) {
    if (!value || typeof value !== 'object') return null;
    const config = getCompanyConfig(value.companyId);
    if (!config) return null;
    const apiUrl = companyDefaultApiUrl(config) || secureApiUrl(value.apiUrl);
    if (!apiUrl) return null;
    const companyName = typeof value.companyName === 'string' && value.companyName.trim() ? value.companyName.trim() : config.name;
    return Object.freeze({ companyId: config.id, apiUrl, companyName });
}

/** The legacy single-company session: Mary at its default URL. */
export function legacyCompany() {
    return sanitizeCompany({ companyId: DEFAULT_COMPANY_ID });
}

function readStoredCompany() {
    const raw = readItem(COMPANY_STORAGE_KEY);
    if (!raw) return null;
    try {
        return sanitizeCompany(JSON.parse(raw));
    } catch {
        return null;
    }
}

function writeStoredCompany(company) {
    const ok = writeItem(COMPANY_STORAGE_KEY, JSON.stringify(company));
    writeItem(LAST_COMPANY_KEY, company.companyId);
    return ok;
}

function hasSignedInCookie() {
    try {
        const token = getAuthCookie();
        return Boolean(token) && token !== 'expired';
    } catch {
        return false;
    }
}

/**
 * Sessions from before the company layer have an auth cookie but no company. Those are
 * Mary's, the only company the admin served: persist Mary so the user stays signed in.
 * Only when this browser never stored another company (see LAST_COMPANY_KEY).
 * @returns {Object|null} The migrated company, or null
 */
function migrateLegacySession() {
    const lastCompanyId = readItem(LAST_COMPANY_KEY);
    if (lastCompanyId && lastCompanyId !== DEFAULT_COMPANY_ID) return null;
    if (!hasSignedInCookie()) return null;
    const company = legacyCompany();
    writeStoredCompany(company);
    return company;
}

function load() {
    if (cached !== undefined) return cached;
    cached = readStoredCompany() || migrateLegacySession() || null;
    return cached;
}

/** The session company, or null when nobody is signed in to a company. */
export function getCompanySync() {
    return load();
}

export function getCompanyIdSync() {
    const company = load();
    return company ? company.companyId : null;
}

/** Config of the session company, or the default company's (Mary) without a session. */
export function getActiveCompany() {
    return getCompanyConfig(getCompanyIdSync()) || getDefaultCompany();
}

export function getActiveCompanyId() {
    return getActiveCompany().id;
}

/**
 * The company the page was loaded for: the router, sidebar and module-level texts are built
 * for it. A sign-in to any other company reloads the page (login.controller.js).
 */
export function getBootCompanyId() {
    if (bootCompanyId === null) bootCompanyId = getActiveCompanyId();
    return bootCompanyId;
}

/**
 * Set and persist the session company. Throws for an unknown company.
 * @param {{ companyId: string, apiUrl?: string, companyName?: string }} value
 * @returns {Object} The stored company
 */
export function setCompany(value) {
    const company = sanitizeCompany(value);
    if (!company) throw new Error('Invalid company');
    writeStoredCompany(company);
    cached = company;
    return company;
}

/** Forget the session company (logout, company switch). Never throws. */
export function clearCompany() {
    cached = null;
    removeItem(COMPANY_STORAGE_KEY);
}

/**
 * API base URL for the current request: the session company's, or Mary's default
 * (`REACT_APP_API_URL || https://service.shelfve.com`) without a session.
 * @returns {string}
 */
export function getApiUrlSync() {
    const company = load();
    return (company && company.apiUrl) || getDefaultApiUrl();
}

/**
 * `{ 'x-company': id }` for the company the request is for: the session company, or the
 * default (Mary) without one, as the admin always sent. Each API rejects another company's id.
 * @returns {{ 'x-company': string }}
 */
export function companyHeaders() {
    return { 'x-company': getActiveCompanyId() };
}

/**
 * Whether the active company has `feature` (see FEATURES in ./features.js).
 * @param {string} feature
 * @returns {boolean}
 */
export function isFeatureEnabled(feature) {
    return companyHasFeature(getActiveCompanyId(), feature);
}

/**
 * Whether the active company's navigation includes the screen (nav key) `screen`.
 * @param {string} screen
 * @returns {boolean}
 */
export function isScreenEnabled(screen) {
    return companyHasScreen(getActiveCompanyId(), screen);
}

/**
 * Whether the active company's backend has users of type `userType` (role pickers, filters).
 * @param {string} userType
 */
export function isUserTypeEnabled(userType) {
    return (getActiveCompany().userTypes || []).includes(userType);
}

/** A value from the active company's `config` (e.g. `expiryWarningDays`). */
export function companySetting(key, fallback = undefined) {
    const config = getActiveCompany().config || {};
    return Object.prototype.hasOwnProperty.call(config, key) ? config[key] : fallback;
}

/**
 * Register a function that forgets in-memory data on a company reset (module caches such as
 * the product catalog). Returns an unregister function.
 * @param {() => void} handler
 */
export function onCompanyReset(handler) {
    resetHandlers.add(handler);
    return () => resetHandlers.delete(handler);
}

/** Run every reset handler. Used by ./reset.js. Never throws. */
export function runCompanyResetHandlers() {
    for (const handler of resetHandlers) {
        try {
            handler();
        } catch (err) {
            console.warn('Company reset handler failed:', err?.message || err);
        }
    }
}

/** Test helper: forget the in-memory session so the next read goes to storage. */
export function __resetCompanySessionForTests() {
    cached = undefined;
    bootCompanyId = null;
}
